import { useEffect, useState } from "react";
import axios from "axios";
import {
  Plane, Clock, MapPin, Droplets, Moon, Check, X,
  Coffee, Cookie, Sparkles, Cpu, Armchair,
} from "lucide-react";
import { useSelectedCountry } from "@/context/SelectedCountryContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Full card names — no abbreviations.
const CARDS = [
  { key: "priority_pass", name: "Priority Pass" },
  { key: "dream_folks", name: "Dream Folks" },
  { key: "dragon_pass", name: "Dragon Pass" },
  { key: "lounge_key", name: "Lounge Key" },
];

export default function Airports() {
  const { slug, country } = useSelectedCountry();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setData(null);
    setIdx(0);
    axios.get(`${API}/airports/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const airport = data?.airports?.[idx];

  return (
    <div className="fade-in max-w-7xl mx-auto px-6 lg:px-12 py-12" data-testid="airports-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Airport playbook</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#0F172A]">
            Airports
            {country && (
              <span className="ml-3 text-xl sm:text-2xl font-bold text-[#002FA7]">
                · {country.flag} {country.name}
              </span>
            )}
          </h1>
        </div>
      </div>

      {/* Airport selector */}
      {data?.airports?.length > 0 && (
        <div
          data-testid="airport-selector"
          className="flex flex-wrap gap-2 mb-6 border-b border-[#E2E8F0] pb-4"
        >
          {data.airports.map((ap, i) => (
            <button
              key={ap.code}
              data-testid={`airport-tab-${ap.code.toLowerCase()}`}
              onClick={() => setIdx(i)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-2 ${
                i === idx
                  ? "bg-[#0F172A] text-white shadow-md"
                  : "bg-white border border-[#E2E8F0] text-[#0F172A] hover:border-[#002FA7]"
              }`}
            >
              <Plane className="w-3.5 h-3.5" strokeWidth={2} />
              <span className="font-mono">{ap.code}</span>
              <span className="hidden sm:inline">·</span>
              <span className="hidden sm:inline">{ap.city}</span>
            </button>
          ))}
        </div>
      )}

      {loading && <div className="text-[#475569] py-12">Loading…</div>}

      {airport && <AirportDashboard airport={airport} />}

      <p className="mt-8 text-xs text-[#94A3B8] italic">
        Lounge hours and card acceptance change — verify on
        prioritypass.com / dreamfolks.in / dragonpass.com / loungekey.com before you arrive.
      </p>
    </div>
  );
}

function AirportDashboard({ airport }) {
  return (
    <div data-testid={`airport-dashboard-${airport.code.toLowerCase()}`} className="space-y-4">
      {/* Airport header strip */}
      <div className="flex items-center justify-between gap-4 bg-[#0F172A] text-white px-5 py-3 rounded-sm">
        <div className="flex items-center gap-3">
          <Plane className="w-4 h-4" strokeWidth={1.8} />
          <h2 className="font-display text-lg sm:text-xl font-bold tracking-tight">{airport.name}</h2>
        </div>
        <div className="text-xs text-white/70 hidden sm:block">{airport.city}</div>
      </div>

      {/* Main 12-col dashboard grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* LEFT — lounges (col-span-7) */}
        <div className="lg:col-span-7 space-y-3">
          <SectionHeader icon={Sparkles} title="Lounges" count={airport.lounges?.length} />
          <div className="space-y-2.5">
            {airport.lounges.map((l, i) => (
              <LoungeRow key={i} lounge={l} idx={i} airportCode={airport.code} />
            ))}
          </div>
        </div>

        {/* RIGHT — shops + rest (col-span-5) */}
        <div className="lg:col-span-5 space-y-4">
          <div>
            <SectionHeader icon={Cookie} title="Best shops" />
            <div className="grid grid-cols-2 gap-2 mt-2">
              <ShopMini icon={Cookie} title="Food" items={airport.shops?.food} />
              <ShopMini icon={Sparkles} title="Chocolate" items={airport.shops?.chocolate} />
              <ShopMini icon={Coffee} title="Coffee" items={airport.shops?.coffee} />
              <ShopMini icon={Cpu} title="Electronics" items={airport.shops?.electronics} />
            </div>
          </div>

          <div>
            <SectionHeader icon={Armchair} title="Quiet rest-spots" count={airport.rest_spots?.length} />
            <div className="space-y-2 mt-2">
              {(airport.rest_spots || []).map((r, i) => (
                <RestSpotMini key={i} spot={r} airportCode={airport.code} idx={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ icon: Icon, title, count }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-[#002FA7]" strokeWidth={1.8} />
      <h3 className="overline text-[#002FA7]">{title}</h3>
      {typeof count === "number" && (
        <span className="text-xs text-[#94A3B8] font-mono">({count})</span>
      )}
    </div>
  );
}

function LoungeRow({ lounge, idx, airportCode }) {
  return (
    <div
      data-testid={`lounge-${airportCode.toLowerCase()}-${idx}`}
      className="p-3.5 border border-[#E2E8F0] rounded-sm bg-white hover:border-[#002FA7]/40 transition-colors"
    >
      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <h4 className="font-display text-[15px] font-bold tracking-tight text-[#0F172A] leading-tight">
            {lounge.name}
          </h4>
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 mt-1 text-[11px] text-[#475569]">
            <span className="inline-flex items-center gap-1">
              <MapPin className="w-3 h-3" strokeWidth={2} />
              {lounge.terminal}
            </span>
            <span className="text-[#002FA7] font-semibold">{lounge.near_gate}</span>
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3 h-3" strokeWidth={2} />
              {lounge.hours}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <FacilityChip on={lounge.shower} icon={Droplets} label="Shower" />
          <FacilityChip on={lounge.sleep} icon={Moon} label="Sleep" />
        </div>
      </div>

      {/* Card acceptance — full names */}
      <div className="flex flex-wrap gap-1.5">
        {CARDS.map(({ key, name }) => {
          const ok = !!lounge.access?.[key];
          return (
            <span
              key={key}
              data-testid={`access-${airportCode.toLowerCase()}-${idx}-${key}`}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                ok
                  ? "bg-[#DCFCE7] text-[#15803D] border border-[#86EFAC]"
                  : "bg-[#FEE2E2] text-[#991B1B] border border-[#FECACA] line-through decoration-[#991B1B]/50"
              }`}
            >
              {ok ? <Check className="w-2.5 h-2.5" strokeWidth={3} /> : <X className="w-2.5 h-2.5" strokeWidth={3} />}
              {name}
            </span>
          );
        })}
      </div>

      {lounge.notes && (
        <p className="text-[11px] text-[#475569] leading-snug mt-2">{lounge.notes}</p>
      )}
    </div>
  );
}

function FacilityChip({ on, icon: Icon, label }) {
  return (
    <span
      title={`${label}: ${on ? "Yes" : "No"}`}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
        on
          ? "bg-[#EEF2FF] text-[#002FA7] border border-[#002FA7]/30"
          : "bg-[#F1F5F9] text-[#94A3B8] border border-[#E2E8F0]"
      }`}
    >
      <Icon className="w-2.5 h-2.5" strokeWidth={2} />
      {label}
    </span>
  );
}

function ShopMini({ icon: Icon, title, items }) {
  const list = items || [];
  return (
    <div
      data-testid={`shop-mini-${title.toLowerCase()}`}
      className="p-3 rounded-sm border border-[#E2E8F0] bg-[#F8F9FA] min-h-[90px]"
    >
      <div className="flex items-center gap-1.5 mb-2 text-[#002FA7]">
        <Icon className="w-3.5 h-3.5" strokeWidth={1.8} />
        <h4 className="font-display font-bold tracking-tight text-[12px]">{title}</h4>
      </div>
      <ul className="space-y-2">
        {list.length === 0 && (
          <li className="text-[11px] text-[#94A3B8] italic">No data</li>
        )}
        {list.map((s, i) => (
          <li key={i} className="text-[11px] leading-tight">
            <div className="font-semibold text-[#0F172A]">{s.name}</div>
            <div className="text-[10px] text-[#002FA7] font-medium">{s.near_gate}</div>
            <div className="text-[10px] text-[#475569]">{s.terminal}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RestSpotMini({ spot, airportCode, idx }) {
  return (
    <div
      data-testid={`rest-spot-${airportCode.toLowerCase()}-${idx}`}
      className="p-3 border border-[#E2E8F0] rounded-sm bg-[#FFFBEB]"
    >
      <div className="flex items-start gap-1.5 mb-1">
        <MapPin className="w-3 h-3 text-[#92400E] mt-0.5 shrink-0" strokeWidth={2} />
        <div className="font-display font-bold text-[#0F172A] tracking-tight text-[12px] leading-tight">
          {spot.location}
        </div>
      </div>
      <div className="ml-4 text-[10px] text-[#92400E] font-semibold uppercase tracking-wider mb-1">
        {spot.near_gate}
      </div>
      <p className="ml-4 text-[11px] text-[#475569] leading-snug">{spot.description}</p>
    </div>
  );
}
